import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAccount, useConnect } from 'wagmi';
import { Web3Provider } from '../providers/Web3Provider';
import {
  Shield, ShieldCheck, Warning, CheckCircle, Clock, Truck, Package,
  ArrowSquareOut, Copy, ArrowCounterClockwise, ShareNetwork, TwitterLogo,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:              { label: 'Pending',      color: '#F59E0B', icon: Clock },
  pending_verification: { label: 'Verifying',    color: '#F59E0B', icon: Clock },
  paid:                 { label: 'Paid',         color: '#007AFF', icon: CheckCircle },
  shipped:              { label: 'Shipped',      color: '#8B5CF6', icon: Truck },
  completed:            { label: 'Completed',    color: '#10B981', icon: CheckCircle },
  canceled:             { label: 'Canceled',     color: '#EF4444', icon: Warning },
};

const ESCROW_META = {
  held:     { label: 'In Escrow',           color: '#007AFF', icon: Shield       },
  released: { label: 'Released to seller',  color: '#10B981', icon: ShieldCheck  },
  refunded: { label: 'Refunded to buyer',   color: '#94A3B8', icon: ArrowCounterClockwise },
  disputed: { label: 'Under Dispute',       color: '#EF4444', icon: Warning      },
};

const explorerTx = (chain, hash) => !hash ? null :
  chain === 'base' ? `https://basescan.org/tx/${hash}` : `https://etherscan.io/tx/${hash}`;

const shortHash = (h) => h ? `${h.slice(0, 10)}…${h.slice(-8)}` : '';

const OrderStatusInner = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { address: connectedWallet, isConnected } = useAccount();
  const { connectors, connect } = useConnect();

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setOrder(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Order not found — check your URL' : 'Failed to load order');
    } finally { setLoading(false); }
  };

  const connectWalletMatchingBuyer = () => {
    // Try injected first
    const injected = connectors.find(c => c.id === 'metaMaskSDK' || c.id === 'metamask' || c.type === 'injected');
    const coinbase = connectors.find(c => c.id === 'coinbaseWallet' || c.id === 'coinbaseWalletSDK');
    const target = injected || coinbase || connectors[0];
    if (target) connect({ connector: target });
  };

  const copy = (label, text) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };

  const submitDispute = async () => {
    if (!reason.trim() || reason.trim().length < 10) {
      toast.error('Please describe the issue in at least 10 characters');
      return;
    }
    if (!connectedWallet) {
      toast.error('Connect your wallet first so we can verify you own this order');
      return;
    }
    if ((connectedWallet || '').toLowerCase() !== (order.wallet_address || '').toLowerCase()) {
      toast.error('Connected wallet does not match this order\'s buyer wallet');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/agent/orders/${orderId}/dispute`, {
        reason: reason.trim(),
        wallet_address: connectedWallet,
      });
      toast.success('Dispute filed — escrow is now locked pending admin review');
      setDisputeOpen(false);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dispute submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <Frame><p className="text-center text-[#94A3B8] py-16">Loading order…</p></Frame>;
  if (error)   return <Frame><p className="text-center text-[#EF4444] py-16">{error}</p></Frame>;
  if (!order)  return null;

  const sMeta = STATUS_META[order.status] || STATUS_META.pending;
  const eMeta = ESCROW_META[order.escrow_state];
  const SIcon = sMeta.icon;
  const EIcon = eMeta?.icon;

  const buyerMatches = connectedWallet && (connectedWallet.toLowerCase() === (order.wallet_address || '').toLowerCase());
  const canDispute = ['held'].includes(order.escrow_state);

  return (
    <Frame>
      {/* Headline */}
      <div className="mb-6">
        <p className="text-xs text-[#94A3B8] font-['JetBrains_Mono']">Order</p>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-3xl font-['JetBrains_Mono'] font-bold text-cyan-300">{order.id}</h1>
          <span className="px-3 py-1 rounded text-xs font-bold font-['JetBrains_Mono'] inline-flex items-center gap-1"
                style={{ background: `${sMeta.color}22`, color: sMeta.color }}>
            <SIcon size={12} weight="fill" /> {sMeta.label}
          </span>
          {eMeta && (
            <span className="px-3 py-1 rounded text-xs font-bold font-['JetBrains_Mono'] inline-flex items-center gap-1"
                  style={{ background: `${eMeta.color}22`, color: eMeta.color }}
                  data-testid="public-escrow-badge">
              <EIcon size={12} weight="fill" /> {eMeta.label}
            </span>
          )}
        </div>
      </div>

      {/* Escrow callout */}
      {order.escrow_state === 'held' && (
        <InfoCard color="#007AFF" icon={<Shield size={22} weight="fill" />}>
          <p className="font-['JetBrains_Mono'] font-bold">Funds held in escrow</p>
          <p className="text-xs text-[#94A3B8]">Your payment is received and verified on-chain. It will release to the seller only when they enter a tracking number. If something goes wrong, you can open a dispute — funds stay locked until resolved.</p>
        </InfoCard>
      )}
      {order.escrow_state === 'released' && (
        <InfoCard color="#10B981" icon={<ShieldCheck size={22} weight="fill" />}>
          <p className="font-['JetBrains_Mono'] font-bold">Shipped &amp; escrow released</p>
          <p className="text-xs text-[#94A3B8]">Tracking was entered — your order is on its way.</p>
        </InfoCard>
      )}
      {order.escrow_state === 'disputed' && (
        <InfoCard color="#EF4444" icon={<Warning size={22} weight="fill" />}>
          <p className="font-['JetBrains_Mono'] font-bold">Dispute filed</p>
          {order.disputed_at && <p className="text-xs text-[#94A3B8]">Filed {new Date(order.disputed_at).toLocaleString()}. Status: <b>{order.dispute_status || 'pending review'}</b>.</p>}
          {order.dispute_reason && <p className="text-xs italic text-[#94A3B8] mt-1">"{order.dispute_reason}"</p>}
        </InfoCard>
      )}
      {order.escrow_state === 'refunded' && order.refund_tx_hash && (
        <InfoCard color="#94A3B8" icon={<ArrowCounterClockwise size={22} weight="fill" />}>
          <p className="font-['JetBrains_Mono'] font-bold">Refunded — verify on-chain</p>
          <a href={explorerTx(order.refund_chain || order.chain, order.refund_tx_hash)} target="_blank" rel="noreferrer"
             className="text-xs text-[#007AFF] hover:text-white break-all font-mono">
            {order.refund_tx_hash} ↗
          </a>
        </InfoCard>
      )}

      {/* Items */}
      <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 mb-4">
        <h2 className="text-sm font-['JetBrains_Mono'] text-[#94A3B8] mb-2">Items</h2>
        {(order.items || []).map((it, idx) => (
          <div key={idx} className="flex justify-between py-1.5 border-b border-[#1E293B] last:border-0 gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold font-mono text-white truncate">{it.part_number}</p>
              <p className="text-xs text-[#94A3B8]">qty {it.quantity} × ${Number(it.unit_price).toFixed(4)}</p>
            </div>
            <p className="text-sm text-[#10B981] font-bold">${Number(it.line_total).toFixed(2)}</p>
          </div>
        ))}
        <div className="flex justify-between pt-2 mt-2 border-t border-[#1E293B]">
          <p className="text-xs text-[#94A3B8]">Subtotal</p>
          <p className="text-sm text-white">${Number(order.subtotal || 0).toFixed(2)}</p>
        </div>
        <div className="flex justify-between">
          <p className="text-xs text-[#94A3B8]">Shipping ({order.shipping_method})</p>
          <p className="text-sm text-white">${Number(order.shipping_cost || 0).toFixed(2)}</p>
        </div>
        <div className="flex justify-between mt-2 pt-2 border-t border-[#10B981]/30">
          <p className="font-['JetBrains_Mono'] text-white">Total</p>
          <p className="font-['JetBrains_Mono'] text-xl text-[#10B981] font-bold">${Number(order.total_usd || 0).toFixed(2)} {order.payment_token}</p>
        </div>
      </div>

      {/* On-chain info */}
      <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 mb-4 space-y-2 text-xs">
        <h2 className="text-sm font-['JetBrains_Mono'] text-[#94A3B8] mb-1">On-chain</h2>
        <OnChainRow label="Chain"         value={order.chain?.toUpperCase()} />
        <OnChainRow label="Payment tx"    value={shortHash(order.payment_tx_hash)} href={explorerTx(order.chain, order.payment_tx_hash)} copy={order.payment_tx_hash} onCopy={copy} />
        <OnChainRow label="Buyer wallet"  value={`${order.wallet_address?.slice(0, 8)}…${order.wallet_address?.slice(-6)}`} copy={order.wallet_address} onCopy={copy} />
        {order.tracking_number && <OnChainRow label="Tracking #" value={order.tracking_number} copy={order.tracking_number} onCopy={copy} />}
      </div>

      {/* Dispute button */}
      {canDispute && (
        <div className="bg-[#1a0f0f] border border-[#EF4444]/40 rounded-lg p-4 space-y-3" data-testid="dispute-cta">
          <div className="flex items-start gap-3">
            <Warning size={22} className="text-[#EF4444] flex-shrink-0 mt-0.5" weight="fill" />
            <div className="text-sm text-[#94A3B8]">
              <p className="text-white font-['JetBrains_Mono'] font-bold">Something wrong with your order?</p>
              <p>Open a dispute and escrow stays locked until it's resolved. You'll need to connect the same wallet you paid from to prove ownership.</p>
            </div>
          </div>
          <Button onClick={() => setDisputeOpen(true)}
                  className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white font-['JetBrains_Mono']"
                  data-testid="open-dispute-btn">
            <Warning size={16} className="mr-2" weight="fill" /> Open a dispute
          </Button>
        </div>
      )}

      {/* Share my purchase */}
      <SharePurchaseCard orderId={orderId} order={order} />

      {/* Dispute dialog */}
      <Dialog open={disputeOpen} onOpenChange={(o) => !submitting && setDisputeOpen(o)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <Warning size={20} weight="fill" /> Open a dispute
            </DialogTitle>
            <DialogDescription className="text-xs text-[#94A3B8]">
              Connect the wallet that paid for this order and describe what went wrong. Escrow will lock until an admin resolves the dispute.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#94A3B8]">
              Disputes lock the seller's escrow until an admin resolves them by release or refund (with verifiable on-chain refund tx).
            </p>

            {/* Wallet auth */}
            {!isConnected && (
              <div className="bg-[#0B1120] border border-[#F59E0B]/40 rounded p-3 space-y-2">
                <p className="text-xs text-[#F59E0B] font-['JetBrains_Mono']">1. Connect the wallet you paid from</p>
                <Button onClick={connectWalletMatchingBuyer}
                        className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-['JetBrains_Mono']"
                        data-testid="dispute-connect-wallet">
                  Connect wallet
                </Button>
              </div>
            )}
            {isConnected && !buyerMatches && (
              <div className="bg-[#1a0f0f] border border-[#EF4444]/40 rounded p-3 space-y-1 text-xs">
                <p className="text-[#EF4444] font-['JetBrains_Mono']">⚠ Wrong wallet connected</p>
                <p className="text-[#94A3B8]">Connected: <code className="text-cyan-300">{connectedWallet?.slice(0,8)}…{connectedWallet?.slice(-4)}</code></p>
                <p className="text-[#94A3B8]">Order's buyer: <code className="text-cyan-300">{order.wallet_address?.slice(0,8)}…{order.wallet_address?.slice(-4)}</code></p>
                <p className="text-[#94A3B8]">Switch wallets in MetaMask / Coinbase Wallet and retry.</p>
              </div>
            )}
            {isConnected && buyerMatches && (
              <div className="bg-[#0B1120] border border-[#10B981]/40 rounded p-3 text-xs">
                <p className="text-[#10B981] font-['JetBrains_Mono'] flex items-center gap-1">
                  <CheckCircle size={12} weight="fill" /> Wallet ownership verified
                </p>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8] font-['JetBrains_Mono']">2. What went wrong? (10+ chars)</label>
              <Textarea value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Tracking shows delivered but I never received the package. DHL photo proof doesn't match my address."
                        rows={4}
                        className="bg-transparent border-[#334155]"
                        data-testid="dispute-reason-input" />
              <p className="text-[10px] text-[#64748B]">{reason.length} / 2000 chars</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)} disabled={submitting} className="border-[#334155]">Cancel</Button>
            <Button onClick={submitDispute}
                    disabled={submitting || !isConnected || !buyerMatches || reason.trim().length < 10}
                    className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40"
                    data-testid="submit-dispute-btn">
              {submitting ? 'Filing…' : 'File dispute → lock escrow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Frame>
  );
};

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#05050A] text-white">
    <header className="border-b border-white/10 px-4 py-3">
      <Link to="/" className="font-['JetBrains_Mono'] font-bold text-cyan-300">← ServerMemoryRFQ</Link>
    </header>
    <main className="max-w-2xl mx-auto px-4 py-8">{children}</main>
    <Footer />
  </div>
);

const InfoCard = ({ color, icon, children }) => (
  <div className="rounded-lg p-4 mb-4 flex items-start gap-3"
       style={{ background: `${color}11`, border: `1px solid ${color}55` }}>
    <div style={{ color }}>{icon}</div>
    <div className="flex-1 min-w-0" style={{ color }}>{children}</div>
  </div>
);

const OnChainRow = ({ label, value, href, copy, onCopy }) => (
  <div className="flex justify-between items-center gap-2">
    <span className="text-[#64748B] uppercase text-[10px] font-['JetBrains_Mono']">{label}</span>
    <div className="flex items-center gap-1 min-w-0">
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-white truncate font-mono text-xs">{value}</a>
      ) : (
        <code className="text-cyan-300 truncate font-mono text-xs">{value}</code>
      )}
      {copy && (
        <button onClick={() => onCopy(label, copy)} className="text-[#94A3B8] hover:text-white p-0.5" aria-label={`Copy ${label}`}>
          <Copy size={12} />
        </button>
      )}
      {href && (
        <a href={href} target="_blank" rel="noreferrer" className="text-[#007AFF] hover:text-white p-0.5">
          <ArrowSquareOut size={12} />
        </a>
      )}
    </div>
  </div>
);
// --- Share my purchase card ---
const SharePurchaseCard = ({ orderId, order }) => {
  if (!order || !['held', 'released'].includes(order.escrow_state)) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://servermemoryrfq.com';
  const shareUrl = `${origin}/api/share/order/${orderId}`;
  const items = order.items || [];
  const firstPn = items[0]?.part_number || 'memory';
  const qty = items.reduce((s, i) => s + Number(i.quantity || 1), 0);
  const amt = Number(order.total_usd || 0).toFixed(2);
  const tweet = `Just bought ${qty}× ${firstPn} for $${amt} ${order.payment_token || 'USDC'} on @servermemoryrfq — settled on-chain via x402 on Base. AI agents can buy server memory now.`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(shareUrl)}`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied — paste anywhere for live preview');
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  };

  const useNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My ServerMemoryRFQ purchase', text: tweet, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      window.open(twitterUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#0F2027] via-[#1A2F38] to-[#0B1120] border border-cyan-500/30 rounded-lg p-4 space-y-3"
         data-testid="share-purchase-card">
      <div className="flex items-start gap-3">
        <ShareNetwork size={22} className="text-cyan-300 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="text-sm text-[#94A3B8] flex-1">
          <p className="text-white font-['JetBrains_Mono'] font-bold">Share your purchase</p>
          <p>Every share is a verified, on-chain receipt — perfect for proving you're an agent-native buyer.</p>
        </div>
      </div>
      <img src={`${API_URL}/api/og/order/${orderId}.png`}
           alt="Order share card preview"
           className="w-full rounded border border-cyan-500/20"
           data-testid="share-card-preview" />
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={useNative}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-['JetBrains_Mono']"
                data-testid="share-purchase-btn">
          <ShareNetwork size={14} weight="fill" className="mr-1" /> Share
        </Button>
        <Button onClick={() => window.open(twitterUrl, '_blank', 'noopener,noreferrer')}
                variant="outline"
                className="border-[#334155] text-white hover:bg-white/5 font-['JetBrains_Mono']"
                data-testid="share-twitter-btn">
          <TwitterLogo size={14} weight="fill" className="mr-1" /> Post to X
        </Button>
      </div>
      <button onClick={copyShareLink}
              className="text-[10px] text-[#64748B] hover:text-cyan-300 font-mono w-full text-center break-all"
              data-testid="copy-share-link">
        {shareUrl} · tap to copy
      </button>
    </div>
  );
};



const OrderStatusPage = () => (
  <Web3Provider>
    <OrderStatusInner />
  </Web3Provider>
);

export default OrderStatusPage;
